import protobuf from 'protobufjs';

/**
 * Pre-compiled Protobuf JSON Root descriptor for Meshtastic protocol.
 * Using an embedded JSON descriptor ensures zero network calls and full offline execution.
 * Aligned with official meshtastic/protobufs definitions.
 */
export const meshtasticProtoJson: protobuf.INamespace = {
  nested: {
    meshtastic: {
      nested: {
        PortNum: {
          values: {
            UNKNOWN_APP: 0,
            TEXT_MESSAGE_APP: 1,
            REMOTE_HARDWARE_APP: 2,
            POSITION_APP: 3,
            NODEINFO_APP: 4,
            ROUTING_APP: 5,
            ADMIN_APP: 6,
            TEXT_MESSAGE_COMPRESSED_APP: 7,
            WAYPOINT_APP: 8,
            AUDIO_APP: 9,
            DETECTION_SENSOR_APP: 10,
            REPLY_APP: 32,
            IP_TUNNEL_APP: 33,
            PAXCOUNTER_APP: 34,
            SERIAL_APP: 64,
            STORE_FORWARD_APP: 65,
            RANGE_TEST_APP: 66,
            TELEMETRY_APP: 67,
            ZPS_APP: 68,
            SIMULATOR_APP: 69,
            TRACEROUTE_APP: 70,
            NEIGHBORINFO_APP: 71,
            MAP_REPORT_APP: 73,
            PRIVATE_APP: 256,
            ATAK_PLUGIN: 257,
            MAX: 511
          }
        },
        Priority: {
          values: {
            UNSET: 0,
            MIN: 1,
            BACKGROUND: 10,
            DEFAULT: 64,
            RELIABLE: 70,
            RESPONSE: 80,
            HIGH: 100,
            ALERT: 110,
            ACK: 120,
            MAX: 127
          }
        },
        User: {
          fields: {
            id: { type: 'string', id: 1 },
            longName: { type: 'string', id: 2 },
            shortName: { type: 'string', id: 3 },
            macaddr: { type: 'bytes', id: 4 },
            hwModel: { type: 'uint32', id: 5 },
            isLicensed: { type: 'bool', id: 6 },
            role: { type: 'uint32', id: 7 },
            publicKey: { type: 'bytes', id: 8 },
            isUnmessagable: { type: 'bool', id: 9 }
          }
        },
        Position: {
          fields: {
            latitudeI: { type: 'sfixed32', id: 1 },
            longitudeI: { type: 'sfixed32', id: 2 },
            altitude: { type: 'int32', id: 3 },
            time: { type: 'fixed32', id: 4 },
            locationSource: { type: 'uint32', id: 5 },
            altitudeSource: { type: 'uint32', id: 6 },
            timestamp: { type: 'fixed32', id: 7 },
            timestampMillisAdjust: { type: 'int32', id: 8 },
            altitudeHae: { type: 'sint32', id: 9 },
            altitudeGeoidalSeparation: { type: 'sint32', id: 10 },
            pdop: { type: 'uint32', id: 11 },
            hdop: { type: 'uint32', id: 12 },
            vdop: { type: 'uint32', id: 13 },
            gpsAccuracy: { type: 'uint32', id: 14 },
            groundSpeed: { type: 'uint32', id: 15 },
            groundTrack: { type: 'uint32', id: 16 },
            fixQuality: { type: 'uint32', id: 17 },
            fixType: { type: 'uint32', id: 18 },
            satsInView: { type: 'uint32', id: 19 },
            sensorId: { type: 'uint32', id: 20 },
            nextUpdate: { type: 'uint32', id: 21 },
            seqNumber: { type: 'uint32', id: 22 },
            precisionBits: { type: 'uint32', id: 23 }
          }
        },
        Data: {
          fields: {
            portnum: { type: 'PortNum', id: 1 },
            payload: { type: 'bytes', id: 2 },
            wantResponse: { type: 'bool', id: 3 },
            dest: { type: 'fixed32', id: 4 },
            source: { type: 'fixed32', id: 5 },
            requestId: { type: 'fixed32', id: 6 },
            replyId: { type: 'fixed32', id: 7 },
            emoji: { type: 'fixed32', id: 8 },
            bitfield: { type: 'uint32', id: 9 }
          }
        },
        MeshPacket: {
          oneofs: {
            payloadVariant: {
              oneof: ['decoded', 'encrypted']
            }
          },
          fields: {
            from: { type: 'fixed32', id: 1 },
            to: { type: 'fixed32', id: 2 },
            channel: { type: 'uint32', id: 3 },
            decoded: { type: 'Data', id: 4 },
            encrypted: { type: 'bytes', id: 5 },
            id: { type: 'fixed32', id: 6 },
            rxTime: { type: 'fixed32', id: 7 },
            rxSnr: { type: 'float', id: 8 },
            hopLimit: { type: 'uint32', id: 9 },
            wantAck: { type: 'bool', id: 10 },
            priority: { type: 'Priority', id: 11 },
            rxRssi: { type: 'int32', id: 12 },
            delayed: { type: 'uint32', id: 13 },
            viaMqtt: { type: 'bool', id: 14 },
            hopStart: { type: 'uint32', id: 15 },
            publicKey: { type: 'bytes', id: 16 },
            pkiEncrypted: { type: 'bool', id: 17 },
            nextHop: { type: 'uint32', id: 18 },
            relayNode: { type: 'uint32', id: 19 },
            txAfter: { type: 'uint32', id: 20 }
          }
        },
        NodeInfo: {
          fields: {
            num: { type: 'uint32', id: 1 },
            user: { type: 'User', id: 2 },
            position: { type: 'Position', id: 3 },
            snr: { type: 'float', id: 4 },
            lastHeard: { type: 'fixed32', id: 5 },
            channel: { type: 'uint32', id: 7 },
            viaMqtt: { type: 'bool', id: 8 },
            hopsAway: { type: 'uint32', id: 9 },
            isFavorite: { type: 'bool', id: 10 },
            isIgnored: { type: 'bool', id: 11 },
            isKeyManuallyVerified: { type: 'bool', id: 12 }
          }
        },
        MyNodeInfo: {
          fields: {
            myNodeNum: { type: 'uint32', id: 1 },
            hasGps: { type: 'bool', id: 2 },
            numBands: { type: 'uint32', id: 3 },
            firmwareVersion: { type: 'string', id: 4 },
            rebootCount: { type: 'uint32', id: 8 },
            minAppVersion: { type: 'uint32', id: 11 },
            deviceId: { type: 'bytes', id: 12 },
            pioEnv: { type: 'string', id: 13 },
            nodedbCount: { type: 'uint32', id: 15 }
          }
        },
        RouteDiscovery: {
          fields: {
            route: { rule: 'repeated', type: 'fixed32', id: 1 },
            snrTowards: { rule: 'repeated', type: 'int32', id: 2 },
            routeBack: { rule: 'repeated', type: 'fixed32', id: 3 },
            snrBack: { rule: 'repeated', type: 'int32', id: 4 }
          }
        },
        FromRadio: {
          oneofs: {
            payloadVariant: {
              oneof: [
                'packet',
                'myInfo',
                'nodeInfo',
                'config',
                'logRecord',
                'configCompleteId',
                'rebooted',
                'moduleConfig',
                'channel',
                'queueStatus',
                'xmodemPacket',
                'metadata',
                'mqttClientProxyMessage',
                'fileInfo',
                'clientNotification'
              ]
            }
          },
          fields: {
            id: { type: 'uint32', id: 1 },
            packet: { type: 'MeshPacket', id: 2 },
            myInfo: { type: 'MyNodeInfo', id: 3 },
            nodeInfo: { type: 'NodeInfo', id: 4 },
            config: { type: 'Config', id: 5 },
            logRecord: { type: 'LogRecord', id: 6 },
            configCompleteId: { type: 'uint32', id: 7 },
            rebooted: { type: 'bool', id: 8 },
            moduleConfig: { type: 'ModuleConfig', id: 9 },
            channel: { type: 'Channel', id: 10 },
            queueStatus: { type: 'QueueStatus', id: 11 },
            xmodemPacket: { type: 'XModem', id: 12 },
            metadata: { type: 'DeviceMetadata', id: 13 },
            mqttClientProxyMessage: { type: 'MqttClientProxyMessage', id: 14 },
            fileInfo: { type: 'FileInfo', id: 15 },
            clientNotification: { type: 'ClientNotification', id: 16 }
          }
        },
        // Stub message descriptors so protobufjs can parse / skip non-essential messages
        Config: {
          fields: {}
        },
        LogRecord: {
          fields: {
            message: { type: 'string', id: 1 },
            time: { type: 'fixed32', id: 2 },
            source: { type: 'string', id: 3 },
            level: { type: 'uint32', id: 4 }
          }
        },
        ModuleConfig: {
          fields: {}
        },
        Channel: {
          fields: {
            index: { type: 'uint32', id: 1 }
          }
        },
        QueueStatus: {
          fields: {
            res: { type: 'int32', id: 1 },
            free: { type: 'uint32', id: 2 },
            maxlen: { type: 'uint32', id: 3 },
            meshPacketId: { type: 'uint32', id: 4 }
          }
        },
        XModem: {
          fields: {}
        },
        DeviceMetadata: {
          fields: {
            firmwareVersion: { type: 'string', id: 1 },
            deviceStateVersion: { type: 'uint32', id: 2 },
            canShutdown: { type: 'bool', id: 3 },
            hasWifi: { type: 'bool', id: 4 },
            hasBluetooth: { type: 'bool', id: 5 },
            hasEthernet: { type: 'bool', id: 6 },
            role: { type: 'uint32', id: 7 },
            positionFlags: { type: 'uint32', id: 8 },
            hwModel: { type: 'uint32', id: 9 },
            hasRemoteHardware: { type: 'bool', id: 10 }
          }
        },
        MqttClientProxyMessage: {
          fields: {}
        },
        FileInfo: {
          fields: {
            fileName: { type: 'string', id: 1 },
            sizeBytes: { type: 'uint32', id: 2 }
          }
        },
        ClientNotification: {
          fields: {
            replyId: { type: 'uint32', id: 1 },
            time: { type: 'fixed32', id: 2 },
            level: { type: 'uint32', id: 3 },
            message: { type: 'string', id: 4 }
          }
        },
        Heartbeat: {
          fields: {
            nonce: { type: 'uint32', id: 1 }
          }
        },
        ToRadio: {
          oneofs: {
            payloadVariant: {
              oneof: ['packet', 'wantConfigId', 'disconnect', 'xmodemPacket', 'mqttClientProxyMessage', 'heartbeat']
            }
          },
          fields: {
            packet: { type: 'MeshPacket', id: 1 },
            wantConfigId: { type: 'uint32', id: 3 },
            disconnect: { type: 'bool', id: 4 },
            xmodemPacket: { type: 'XModem', id: 5 },
            mqttClientProxyMessage: { type: 'MqttClientProxyMessage', id: 6 },
            heartbeat: { type: 'Heartbeat', id: 7 }
          }
        }
      }
    }
  }
};

/**
 * Instantiated protobuf Root for the Meshtastic schema.
 */
export const protoRoot = protobuf.Root.fromJSON(meshtasticProtoJson);
